/**
 * BackgroundMusicModal - Modal for selecting background music
 *
 * Features:
 * - Curated music library with category filters
 * - Selected track preview with play/pause
 * - Volume slider
 * - Loop toggle
 * - Modern, elegant shadcn-style UI
 */

import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { studioBOn } from '@/lib/studioB/flag';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Music,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Repeat,
  Check,
  Loader2,
  AlertCircle,
  Disc3,
} from 'lucide-react';
import {
  getAllResolvedTracks,
  getCategories,
  getLicenseInfo,
} from '@/services/musicLibraryService';
import type { ResolvedLibraryTrack, MusicCategory } from '@/types/musicLibrary';

export interface SelectedTrack {
  id: string;
  title: string;
  url: string;
  duration: number;
  source: 'library' | 'freesound' | 'uploaded' | 'url';
  artist?: string;
  category?: string;
}

export interface MusicConfig {
  track: SelectedTrack;
  volume: number;
  loop: boolean;
}

interface BackgroundMusicModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig?: MusicConfig | null;
  onApply: (config: MusicConfig) => void;
  className?: string;
}

// Category display names and colors
const CATEGORY_CONFIG: Record<
  MusicCategory,
  { label: string; bgClass: string; textClass: string }
> = {
  ambient: { label: 'Ambient', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  uplifting: { label: 'Uplifting', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  elegant: { label: 'Elegant', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  technology: { label: 'Technology', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  corporate: { label: 'Corporate', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  lounge: { label: 'Lounge', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
  classical: { label: 'Classical', bgClass: 'bg-primary/10 hover:bg-primary/20', textClass: 'text-primary dark:text-accent-foreground' },
};

export const BackgroundMusicModal: React.FC<BackgroundMusicModalProps> = ({
  open,
  onOpenChange,
  initialConfig,
  onApply,
  className,
}) => {
  // Library state
  const [tracks, setTracks] = useState<ResolvedLibraryTrack[]>([]);
  const [filteredTracks, setFilteredTracks] = useState<ResolvedLibraryTrack[]>([]);
  const [categories, setCategories] = useState<Record<MusicCategory, { description: string; count: number }> | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<MusicCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [licenseInfo, setLicenseInfo] = useState<{ attribution: string } | null>(null);

  // Selection state
  const [selectedTrack, setSelectedTrack] = useState<SelectedTrack | null>(
    initialConfig?.track || null
  );
  const [volume, setVolume] = useState(initialConfig?.volume ?? 0.3);
  const [loop, setLoop] = useState(initialConfig?.loop ?? true);

  // Preview state
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [isSelectedPreviewPlaying, setIsSelectedPreviewPlaying] = useState(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const selectedPreviewAudioRef = useRef<HTMLAudioElement | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTrack(initialConfig?.track || null);
      setVolume(initialConfig?.volume ?? 0.3);
      setLoop(initialConfig?.loop ?? true);
      setPreviewingId(null);
      setIsSelectedPreviewPlaying(false);
    }
  }, [open, initialConfig]);

  // Initialize audio elements
  useEffect(() => {
    previewAudioRef.current = new Audio();
    previewAudioRef.current.volume = 0.5;
    previewAudioRef.current.addEventListener('ended', () => setPreviewingId(null));
    previewAudioRef.current.addEventListener('error', () => setPreviewingId(null));

    selectedPreviewAudioRef.current = new Audio();
    selectedPreviewAudioRef.current.addEventListener('ended', () => setIsSelectedPreviewPlaying(false));
    selectedPreviewAudioRef.current.addEventListener('error', () => setIsSelectedPreviewPlaying(false));

    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.src = '';
      }
      if (selectedPreviewAudioRef.current) {
        selectedPreviewAudioRef.current.pause();
        selectedPreviewAudioRef.current.src = '';
      }
    };
  }, []);

  // Load library data
  useEffect(() => {
    async function loadLibrary() {
      setIsLoading(true);
      setError(null);

      try {
        const [allTracks, categoryInfo, license] = await Promise.all([
          getAllResolvedTracks(),
          getCategories(),
          getLicenseInfo(),
        ]);

        setTracks(allTracks);
        setFilteredTracks(allTracks);
        setCategories(categoryInfo);
        setLicenseInfo(license);
      } catch (err) {
        console.error('[BackgroundMusicModal] Failed to load library:', err);
        setError('Failed to load music library');
      } finally {
        setIsLoading(false);
      }
    }

    if (open) {
      loadLibrary();
    }
  }, [open]);

  // Filter tracks when category changes
  useEffect(() => {
    if (selectedCategory) {
      setFilteredTracks(tracks.filter((t) => t.category === selectedCategory));
    } else {
      setFilteredTracks(tracks);
    }
  }, [selectedCategory, tracks]);

  // Update selected preview audio
  useEffect(() => {
    if (selectedPreviewAudioRef.current && selectedTrack?.url) {
      selectedPreviewAudioRef.current.src = selectedTrack.url;
      selectedPreviewAudioRef.current.volume = volume;
      selectedPreviewAudioRef.current.loop = loop;
    }
  }, [selectedTrack?.url]);

  useEffect(() => {
    if (selectedPreviewAudioRef.current) {
      selectedPreviewAudioRef.current.volume = volume;
      selectedPreviewAudioRef.current.loop = loop;
    }
  }, [volume, loop]);

  // Stop previews when modal closes
  useEffect(() => {
    if (!open) {
      previewAudioRef.current?.pause();
      selectedPreviewAudioRef.current?.pause();
      setPreviewingId(null);
      setIsSelectedPreviewPlaying(false);
    }
  }, [open]);

  const handleCategoryClick = useCallback((category: MusicCategory) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
  }, []);

  const handleTrackPreview = useCallback((track: ResolvedLibraryTrack) => {
    if (!previewAudioRef.current) return;

    // Stop selected preview if playing
    if (selectedPreviewAudioRef.current && !selectedPreviewAudioRef.current.paused) {
      selectedPreviewAudioRef.current.pause();
      setIsSelectedPreviewPlaying(false);
    }

    if (previewingId === track.id) {
      previewAudioRef.current.pause();
      setPreviewingId(null);
    } else {
      previewAudioRef.current.src = track.url;
      previewAudioRef.current.play().catch(console.error);
      setPreviewingId(track.id);
    }
  }, [previewingId]);

  const handleTrackSelect = useCallback((track: ResolvedLibraryTrack) => {
    // Stop list preview
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      setPreviewingId(null);
    }

    setSelectedTrack({
      id: track.id,
      title: track.title,
      url: track.url,
      duration: track.duration,
      source: 'library',
      artist: track.artist,
      category: track.category,
    });
  }, []);

  const toggleSelectedPreview = useCallback(() => {
    if (!selectedPreviewAudioRef.current || !selectedTrack?.url) return;

    // Stop list preview
    if (previewAudioRef.current && !previewAudioRef.current.paused) {
      previewAudioRef.current.pause();
      setPreviewingId(null);
    }

    if (isSelectedPreviewPlaying) {
      selectedPreviewAudioRef.current.pause();
      setIsSelectedPreviewPlaying(false);
    } else {
      selectedPreviewAudioRef.current.play().catch(console.error);
      setIsSelectedPreviewPlaying(true);
    }
  }, [isSelectedPreviewPlaying, selectedTrack?.url]);

  const handleVolumeChange = useCallback((values: number[]) => {
    setVolume(values[0] / 100);
  }, []);

  const handleApply = useCallback(() => {
    if (!selectedTrack) return;

    previewAudioRef.current?.pause();
    selectedPreviewAudioRef.current?.pause();

    onApply({
      track: selectedTrack,
      volume,
      loop,
    });

    onOpenChange(false);
  }, [selectedTrack, volume, loop, onApply, onOpenChange]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const volumePercent = Math.round(volume * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        {...(studioBOn() ? { 'data-b-surface': '' } : {})}
        className={cn(
          'max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden',
          studioBOn() && 'rounded-[var(--b-radius-sheet)] border-b-line bg-b-elevated',
          className
        )}
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2.5 text-lg">
            <div className="p-2 rounded-lg bg-primary/10">
              <Music className="w-5 h-5 text-primary" />
            </div>
            Background Music
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose music that plays across all slides in your video
          </DialogDescription>
        </DialogHeader>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0 border-t border-border/50">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">Loading music library...</p>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="p-3 rounded-full bg-destructive/10 mb-4">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              {/* Category Filters */}
              <div className="px-6 py-4 flex-shrink-0 bg-muted/30">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                      !selectedCategory
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-background hover:bg-muted border border-border/50 text-muted-foreground hover:text-foreground'
                    )}
                  >
                    All Tracks
                  </button>
                  {categories &&
                    Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                      const category = key as MusicCategory;
                      const info = categories[category];
                      if (!info || info.count === 0) return null;

                      const isSelected = selectedCategory === category;
                      return (
                        <button
                          key={category}
                          onClick={() => handleCategoryClick(category)}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                            isSelected
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : cn(config.bgClass, config.textClass, 'border border-transparent')
                          )}
                        >
                          {config.label}
                          <span className="ml-1.5 opacity-70">{info.count}</span>
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Track List */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="p-4 space-y-1">
                  {filteredTracks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Disc3 className="w-10 h-10 text-muted-foreground/30 mb-3" />
                      <p className="text-sm text-muted-foreground">No tracks in this category</p>
                    </div>
                  ) : (
                    filteredTracks.map((track) => {
                      const isSelected = selectedTrack?.id === track.id;
                      const isPreviewing = previewingId === track.id;
                      const categoryConfig = CATEGORY_CONFIG[track.category as MusicCategory];

                      return (
                        <div
                          key={track.id}
                          className={cn(
                            'group flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer',
                            isSelected
                              ? 'bg-primary/10 ring-1 ring-primary/30'
                              : 'hover:bg-muted/60'
                          )}
                          onClick={() => handleTrackSelect(track)}
                        >
                          {/* Play/Pause Button */}
                          <button
                            className={cn(
                              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                              isPreviewing
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted hover:bg-primary/20 text-muted-foreground hover:text-primary'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTrackPreview(track);
                            }}
                          >
                            {isPreviewing ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4 ml-0.5" />
                            )}
                          </button>

                          {/* Track Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">{track.title}</p>
                              {isSelected && (
                                <Badge className="bg-primary/20 text-primary border-0 text-[10px] px-1.5">
                                  Selected
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-muted-foreground">
                                {track.durationFormatted}
                              </span>
                              <span className="text-xs text-muted-foreground/50">•</span>
                              <span className="text-xs text-muted-foreground/70">
                                {track.artist}
                              </span>
                            </div>
                          </div>

                          {/* Mood Tags */}
                          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
                            {track.mood.slice(0, 2).map((mood) => (
                              <span
                                key={mood}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                              >
                                {mood}
                              </span>
                            ))}
                          </div>

                          {/* Category */}
                          {categoryConfig && (
                            <span
                              className={cn(
                                'text-[10px] px-2.5 py-1 rounded-full flex-shrink-0',
                                categoryConfig.bgClass,
                                categoryConfig.textClass
                              )}
                            >
                              {categoryConfig.label}
                            </span>
                          )}

                          {/* Select Indicator */}
                          <div
                            className={cn(
                              'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'border-2 border-muted-foreground/20 group-hover:border-primary/50'
                            )}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* License Footer */}
              <div className="px-6 py-2 border-t border-border/30 bg-muted/20 flex-shrink-0">
                <p className="text-[10px] text-muted-foreground/60 text-center">
                  {licenseInfo?.attribution || 'Music by Kevin MacLeod (incompetech.com)'} • Licensed under CC BY 4.0
                </p>
              </div>
            </>
          )}
        </div>

        {/* Selected Track Panel */}
        {selectedTrack && (
          <div className="px-6 py-5 border-t border-border bg-gradient-to-b from-muted/50 to-muted/30 flex-shrink-0">
            <div className="flex items-start gap-4">
              {/* Preview Button */}
              <button
                onClick={toggleSelectedPreview}
                className={cn(
                  'w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all',
                  isSelectedPreviewPlaying
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                    : 'bg-primary/10 hover:bg-primary/20 text-primary'
                )}
              >
                {isSelectedPreviewPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6 ml-1" />
                )}
              </button>

              {/* Track Info & Controls */}
              <div className="flex-1 min-w-0 space-y-4">
                {/* Track Details */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Selected Track
                    </span>
                  </div>
                  <p className="text-sm font-medium truncate">{selectedTrack.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedTrack.artist && `${selectedTrack.artist} • `}
                    {selectedTrack.duration > 0 && formatDuration(selectedTrack.duration)}
                  </p>
                </div>

                {/* Volume & Loop */}
                <div className="flex items-center gap-6">
                  {/* Volume Slider */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                        {volumePercent === 0 ? (
                          <VolumeX className="w-3.5 h-3.5" />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5" />
                        )}
                        Volume
                      </Label>
                      <span className="text-xs font-medium tabular-nums w-10 text-right">
                        {volumePercent}%
                      </span>
                    </div>
                    <Slider
                      value={[volumePercent]}
                      onValueChange={handleVolumeChange}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-[10px] text-muted-foreground/60">
                      Recommended: 20-40% for background music
                    </p>
                  </div>

                  {/* Loop Toggle */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Label
                      htmlFor="loop-toggle"
                      className="text-xs text-muted-foreground flex items-center gap-1.5 cursor-pointer"
                    >
                      <Repeat className="w-3.5 h-3.5" />
                      Loop
                    </Label>
                    <Switch
                      id="loop-toggle"
                      checked={loop}
                      onCheckedChange={setLoop}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedTrack}
            className="min-w-[120px]"
          >
            <Check className="w-4 h-4 mr-2" />
            Apply Music
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackgroundMusicModal;
